import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../config";
import {
  getVmRecordTdStyle,
  getVmRecordThStyle,
  vmPageShell,
  vmRecordTable,
  vmTableScrollWrap,
} from "./vehicleManagementLayout";
import { dateKeyFromRecord, defaultPeriodFilters, periodLabel } from "../../lib/vmDateFilters";
import {
  buildDieselNotFilledRows,
  buildDieselRecordGridRows,
  buildDieselScheduleUpcoming,
  buildMaintenanceRecordGridRows,
  enrichPerVehicleOverview,
  filterRecordsByPeriod,
  maintenanceWithNextService,
  normalizeDieselRow,
  parseAmount,
  perVehiclePeriodRows,
  summarizeDocuments,
  sumDiesel,
  sumExpenses,
  sumMaintenance,
} from "../../lib/vmOverviewStats";
import VmOverviewRecordTables from "./VmOverviewRecordTables";

const tiles = [
  { label: "Vehicle Onboarding", path: "/vehicle-onboarding", icon: "🚛" },
  { label: "Documents", path: "/vehicle-documents", icon: "📄" },
  { label: "Truck Maintenance", path: "/truck-maintenance", icon: "🔧" },
  { label: "Live Fleet Tracking", path: "/live-fleet-tracking", icon: "📍" },
  { label: "Diesel", path: "/diesel", icon: "⛽" },
  { label: "Expenses", path: "/expenses", icon: "💸" },
];

function fmtInr(n) {
  return `₹${Math.round(n).toLocaleString("en-IN")}`;
}

function mapApiExpenses(apiExpenses) {
  return (apiExpenses || [])
    .filter((r) => (r.expenseType || "").trim() !== "Others")
    .map((r) => ({
      ...r,
      dateKey: dateKeyFromRecord(r, ["date"]),
      amount: parseAmount(r.amount),
    }));
}

export default function VehicleManagement() {
  const nav = useNavigate();
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth <= 768,
  );
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [vehicles, setVehicles] = useState([]);
  const [dieselRows, setDieselRows] = useState([]);
  const [maintRows, setMaintRows] = useState([]);
  const [apiExpenseRows, setApiExpenseRows] = useState([]);

  const [filterMode, setFilterMode] = useState("day");
  const [filterDay, setFilterDay] = useState(() => defaultPeriodFilters("day").filterDay);
  const [filterMonth, setFilterMonth] = useState(() => defaultPeriodFilters("month").filterMonth);
  const [filterYear, setFilterYear] = useState(() => defaultPeriodFilters("year").filterYear);
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [filterVehicle, setFilterVehicle] = useState("");
  const [search, setSearch] = useState("");

  const periodFilters = useMemo(
    () => ({ filterDay, filterMonth, filterYear, filterFrom, filterTo }),
    [filterDay, filterMonth, filterYear, filterFrom, filterTo],
  );

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError("");
      try {
        const base = API_BASE_URL;
        const [vRes, dRes, mRes, eRes] = await Promise.all([
          axios.get(`${base}/onboarding/all-vehicles`),
          axios.get(`${base}/vehicle/diesel`, { params: { officialOnly: "1" } }),
          axios.get(`${base}/vehicle/truck-maintenance`, { withCredentials: true }),
          axios.get(`${base}/vehicle/expenses`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setVehicles(Array.isArray(vRes.data) ? vRes.data : []);
        setDieselRows(
          (Array.isArray(dRes.data) ? dRes.data : []).map(normalizeDieselRow),
        );
        setMaintRows(Array.isArray(mRes.data) ? mRes.data : []);
        setApiExpenseRows(mapApiExpenses(eRes.data));
      } catch (e) {
        if (!cancelled) {
          setLoadError(e?.response?.data?.message || e.message || "Failed to load overview data.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const vehicleList = useMemo(() => {
    const q = search.trim().toLowerCase();
    const fv = filterVehicle.trim().toUpperCase();
    return vehicles.filter((v) => {
      const vn = String(v.vehicleNumber || "").trim();
      if (fv && vn.toUpperCase() !== fv) return false;
      if (!q) return true;
      const hay = [vn, v.registerName, v.ownerName, v.vehicleType].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [vehicles, filterVehicle, search]);

  const vehicleNumbers = useMemo(
    () =>
      [...vehicles]
        .map((v) => String(v.vehicleNumber || "").trim())
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
    [vehicles],
  );

  const scopedDiesel = useMemo(() => {
    let rows = filterRecordsByPeriod(dieselRows, filterMode, periodFilters);
    if (filterVehicle) {
      const fv = filterVehicle.trim().toUpperCase();
      rows = rows.filter((r) => String(r.vehicleNumber || "").toUpperCase() === fv);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter((r) =>
        String(r.vehicleNumber || "").toLowerCase().includes(q),
      );
    }
    return rows;
  }, [dieselRows, filterMode, periodFilters, filterVehicle, search]);

  const scopedMaint = useMemo(() => {
    let rows = maintRows.map((r) => ({
      ...r,
      dateKey: dateKeyFromRecord(r, ["date"]),
    }));
    rows = filterRecordsByPeriod(rows, filterMode, periodFilters);
    if (filterVehicle) {
      const fv = filterVehicle.trim().toUpperCase();
      rows = rows.filter(
        (r) => String(r.truckNo || r.vehicleNumber || "").toUpperCase() === fv,
      );
    }
    return rows;
  }, [maintRows, filterMode, periodFilters, filterVehicle]);

  const scopedExpenses = useMemo(() => {
    let rows = filterRecordsByPeriod(apiExpenseRows, filterMode, periodFilters);
    if (filterVehicle) {
      const fv = filterVehicle.trim().toUpperCase();
      rows = rows.filter((r) => String(r.vehicleNumber || "").toUpperCase() === fv);
    }
    return rows;
  }, [apiExpenseRows, filterMode, periodFilters, filterVehicle]);

  const vehicleByNumber = useMemo(() => {
    const m = new Map();
    for (const v of vehicles) {
      const vn = String(v.vehicleNumber || "").trim();
      if (vn) m.set(vn.toUpperCase(), v);
    }
    return m;
  }, [vehicles]);

  const dieselRecordGrid = useMemo(
    () => buildDieselRecordGridRows(scopedDiesel, vehicleByNumber),
    [scopedDiesel, vehicleByNumber],
  );

  const dieselNotFilled = useMemo(
    () =>
      filterMode === "day"
        ? buildDieselNotFilledRows(vehicleList, scopedDiesel)
        : [],
    [filterMode, vehicleList, scopedDiesel],
  );

  const maintenanceRecordGrid = useMemo(
    () => buildMaintenanceRecordGridRows(scopedMaint),
    [scopedMaint],
  );

  const periodFillStats = useMemo(() => {
    const filledVehicles = new Set(
      scopedDiesel.map((r) => String(r.vehicleNumber || "").trim().toUpperCase()),
    );
    return {
      filled: filledVehicles.size,
      notFilled: Math.max(0, vehicleList.length - filledVehicles.size),
      fills: scopedDiesel.length,
    };
  }, [scopedDiesel, vehicleList.length]);

  const dieselSum = useMemo(() => sumDiesel(scopedDiesel), [scopedDiesel]);
  const maintSum = useMemo(() => sumMaintenance(scopedMaint), [scopedMaint]);
  const expSum = useMemo(() => sumExpenses(scopedExpenses), [scopedExpenses]);

  const docStats = useMemo(
    () => summarizeDocuments(vehicleList, filterMode, periodFilters),
    [vehicleList, filterMode, periodFilters],
  );

  const dieselUpcoming = useMemo(
    () => buildDieselScheduleUpcoming(vehicleList, dieselRows, filterMode, periodFilters),
    [vehicleList, dieselRows, filterMode, periodFilters],
  );

  const serviceUpcoming = useMemo(
    () => maintenanceWithNextService(maintRows, filterMode, periodFilters),
    [maintRows, filterMode, periodFilters],
  );

  const perVehicle = useMemo(
    () =>
      enrichPerVehicleOverview(
        perVehiclePeriodRows(vehicleList, scopedDiesel, scopedMaint, scopedExpenses),
        vehicleList,
        dieselRows,
        filterMode,
        periodFilters,
      ),
    [vehicleList, scopedDiesel, scopedMaint, scopedExpenses, dieselRows, filterMode, periodFilters],
  );

  const recordsSectionTitle =
    filterMode === "day"
      ? "Daily diesel records"
      : filterMode === "month"
        ? "Monthly diesel records"
        : filterMode === "year"
          ? "Yearly diesel records"
          : "Diesel records (period)";

  const maintSectionTitle =
    filterMode === "day"
      ? "Daily maintenance records"
      : filterMode === "month"
        ? "Monthly maintenance records"
        : filterMode === "year"
          ? "Yearly maintenance records"
          : "Maintenance records (period)";

  const clearFilters = () => {
    const d = defaultPeriodFilters("day");
    setFilterMode("day");
    setFilterDay(d.filterDay);
    setFilterMonth(d.filterMonth);
    setFilterYear(d.filterYear);
    setFilterFrom("");
    setFilterTo("");
    setFilterVehicle("");
    setSearch("");
  };

  const S = {
    shell: {
      ...vmPageShell(isMobile, { marginRightPx: isMobile ? 0 : 10 }),
      fontFamily: "Arial, sans-serif",
      backgroundColor: "#fff",
      color: "#000",
      minHeight: "calc(100vh - 70px)",
      boxShadow: "0px 4px 8px rgba(0,0,0,0.1)",
    },
    header: {
      backgroundColor: "#FFC107",
      color: "#000",
      padding: isMobile ? "14px 16px" : "16px 20px",
      fontWeight: "bold",
      fontSize: isMobile ? "17px" : "20px",
      letterSpacing: "1px",
      textTransform: "uppercase",
    },
    pad: { padding: isMobile ? "14px" : "20px" },
    filters: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(160px, 1fr))",
      gap: 12,
      marginBottom: 16,
      alignItems: "end",
    },
    label: { fontWeight: "bold", fontSize: 13, marginBottom: 5, display: "block", color: "#000" },
    input: {
      width: "100%",
      padding: "9px 10px",
      border: "1.5px solid #000",
      borderRadius: 4,
      fontSize: isMobile ? "16px" : "14px",
      boxSizing: "border-box",
      color: "#000",
      backgroundColor: "#fff",
      outline: "none",
    },
    cards: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(auto-fit, minmax(140px, 1fr))",
      gap: 12,
      marginBottom: 20,
    },
    card: {
      background: "#fff",
      borderRadius: 6,
      padding: "14px 16px",
      border: "1.5px solid #000",
    },
    cardTitle: { fontSize: 12, fontWeight: "bold", color: "#555", textTransform: "uppercase", marginBottom: 6 },
    cardValue: { fontSize: 22, fontWeight: "bold", color: "#000" },
    cardSub: { fontSize: 12, color: "#555", marginTop: 4 },
    section: {
      fontWeight: "bold",
      fontSize: 14,
      color: "#000",
      borderBottom: "2px solid #FFC107",
      paddingBottom: 6,
      marginBottom: 12,
      marginTop: 20,
    },
    panel: {
      marginBottom: 16,
    },
    tableScroll: vmTableScrollWrap,
    table: { ...vmRecordTable, fontSize: isMobile ? 12 : 13 },
    th: getVmRecordThStyle(isMobile),
    td: getVmRecordTdStyle(isMobile),
    btnOutline: {
      padding: "9px 16px",
      border: "1.5px solid #000",
      borderRadius: 4,
      fontWeight: "bold",
      cursor: "pointer",
      fontSize: 14,
      backgroundColor: "#fff",
      color: "#000",
    },
    btnYellow: {
      padding: "9px 16px",
      border: "none",
      borderRadius: 4,
      fontWeight: "bold",
      cursor: "pointer",
      fontSize: 14,
      backgroundColor: "#FFC107",
      color: "#000",
    },
    tiles: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3, 1fr)",
      gap: 14,
      marginTop: 24,
    },
    tile: {
      backgroundColor: "#fff",
      borderRadius: 6,
      padding: "22px 12px",
      textAlign: "center",
      cursor: "pointer",
      border: "1.5px solid #000",
    },
    badge: (tone) => ({
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: 4,
      fontSize: 11,
      fontWeight: 700,
      background:
        tone === "bad" ? "#ffebee" : tone === "warn" ? "#fff8e1" : "#e8f5e9",
      color: tone === "bad" ? "#c62828" : tone === "warn" ? "#f57f17" : "#2e7d32",
    }),
  };

  const periodTitle = periodLabel(filterMode, periodFilters);

  return (
    <div style={S.shell}>
      <div style={S.header}>Vehicle Management — Overview</div>
      <div style={S.pad}>
        <div style={S.filters}>
          <div>
            <label style={S.label}>Period</label>
            <select style={S.input} value={filterMode} onChange={(e) => setFilterMode(e.target.value)}>
              <option value="day">Daily</option>
              <option value="month">Monthly</option>
              <option value="year">Yearly</option>
              <option value="all">All time</option>
              <option value="range">Date range</option>
            </select>
          </div>
          {filterMode === "day" ? (
            <div>
              <label style={S.label}>Date</label>
              <input type="date" style={S.input} value={filterDay} onChange={(e) => setFilterDay(e.target.value)} />
            </div>
          ) : null}
          {filterMode === "month" ? (
            <div>
              <label style={S.label}>Month</label>
              <input type="month" style={S.input} value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} />
            </div>
          ) : null}
          {filterMode === "year" ? (
            <div>
              <label style={S.label}>Year</label>
              <input
                type="number"
                style={S.input}
                min={2020}
                max={2035}
                value={filterYear}
                onChange={(e) => setFilterYear(e.target.value)}
              />
            </div>
          ) : null}
          {filterMode === "range" ? (
            <>
              <div>
                <label style={S.label}>From</label>
                <input type="date" style={S.input} value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} />
              </div>
              <div>
                <label style={S.label}>To</label>
                <input type="date" style={S.input} value={filterTo} onChange={(e) => setFilterTo(e.target.value)} />
              </div>
            </>
          ) : null}
          <div>
            <label style={S.label}>Vehicle</label>
            <select style={S.input} value={filterVehicle} onChange={(e) => setFilterVehicle(e.target.value)}>
              <option value="">All vehicles</option>
              {vehicleNumbers.map((vn) => (
                <option key={vn} value={vn}>
                  {vn}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={S.label}>Search</label>
            <input
              style={S.input}
              placeholder="Vehicle / owner…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" style={S.btnOutline} onClick={clearFilters}>
              Clear
            </button>
            <button
              type="button"
              style={S.btnYellow}
              onClick={() => {
                const t = defaultPeriodFilters("day");
                setFilterMode("day");
                setFilterDay(t.filterDay);
              }}
            >
              Today
            </button>
          </div>
        </div>

        <div style={{ fontSize: 13, marginBottom: 12, color: "#333" }}>
          Showing: <b>{periodTitle}</b>
          {filterVehicle ? <> · Vehicle <b>{filterVehicle}</b></> : null}
          {loading ? " · Loading…" : ` · ${vehicleList.length} vehicle(s)`}
        </div>

        {loadError ? (
          <p style={{ color: "#c62828", fontWeight: 600 }} role="alert">
            {loadError}
          </p>
        ) : null}

        <div style={S.cards}>
          <div style={S.card}>
            <div style={S.cardTitle}>Fleet (onboarded)</div>
            <div style={S.cardValue}>{vehicleList.length}</div>
            <div style={S.cardSub}>From onboarding</div>
          </div>
          {filterMode === "day" ? (
            <>
              <div style={S.card}>
                <div style={S.cardTitle}>Vehicles filled</div>
                <div style={S.cardValue}>{periodFillStats.filled}</div>
                <div style={S.cardSub}>{periodFillStats.fills} fill(s) on {filterDay}</div>
              </div>
              <div style={S.card}>
                <div style={S.cardTitle}>Not filled</div>
                <div style={{ ...S.cardValue, color: periodFillStats.notFilled > 0 ? "#c62828" : "#111" }}>
                  {periodFillStats.notFilled}
                </div>
                <div style={S.cardSub}>Onboarded, no diesel entry</div>
              </div>
            </>
          ) : null}
          <div style={S.card}>
            <div style={S.cardTitle}>Diesel</div>
            <div style={S.cardValue}>{fmtInr(dieselSum.amount)}</div>
            <div style={S.cardSub}>
              {dieselSum.count} fill(s) · {dieselSum.litres.toFixed(1)} L · {Math.round(dieselSum.km)} km
            </div>
          </div>
          <div style={S.card}>
            <div style={S.cardTitle}>Truck maintenance</div>
            <div style={S.cardValue}>{fmtInr(maintSum.amount)}</div>
            <div style={S.cardSub}>{maintSum.count} record(s)</div>
          </div>
          <div style={S.card}>
            <div style={S.cardTitle}>Vehicle expenses</div>
            <div style={S.cardValue}>{fmtInr(expSum.amount)}</div>
            <div style={S.cardSub}>{expSum.count} record(s)</div>
          </div>
          <div style={S.card}>
            <div style={S.cardTitle}>Documents</div>
            <div style={S.cardValue}>{docStats.expired + docStats.expiringSoon}</div>
            <div style={S.cardSub}>
              {docStats.expired} expired · {docStats.expiringSoon} expiring ≤30d
            </div>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16 }}>
          <div>
            <div style={S.section}>Scheduled diesel refills</div>
            <div style={S.panel}>
              <div style={S.tableScroll}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Vehicle</th>
                      <th style={S.th}>Next due</th>
                      <th style={S.th}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dieselUpcoming.length === 0 ? (
                      <tr>
                        <td style={S.td} colSpan={3}>
                          No refills due in this period (check onboarding schedule interval).
                        </td>
                      </tr>
                    ) : (
                      dieselUpcoming.slice(0, 12).map((r) => (
                        <tr key={`${r.vehicleNumber}-${r.nextDueYmd}`}>
                          <td style={S.td}>
                            <b>{r.vehicleNumber}</b>
                            <div style={{ fontSize: 11, color: "#666" }}>{r.registerName}</div>
                          </td>
                          <td style={S.td}>
                            {r.nextDueYmd}
                            <div style={{ fontSize: 11 }}>
                              Every {r.intervalDays}d · last {r.lastFillYmd || "—"}
                            </div>
                          </td>
                          <td style={S.td}>
                            <span
                              style={S.badge(
                                r.status === "Overdue" ? "bad" : r.status === "Due soon" ? "warn" : "ok",
                              )}
                            >
                              {r.status}
                              {r.daysLeft < 9999 ? ` (${r.daysLeft}d)` : ""}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={S.section}>Document expiries (period)</div>
            <div style={S.panel}>
              <div style={S.tableScroll}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Vehicle</th>
                      <th style={S.th}>Document</th>
                      <th style={S.th}>Expiry</th>
                    </tr>
                  </thead>
                  <tbody>
                    {docStats.upcoming.length === 0 ? (
                      <tr>
                        <td style={S.td} colSpan={3}>
                          No expiries in this period (or all valid).
                        </td>
                      </tr>
                    ) : (
                      docStats.upcoming.slice(0, 12).map((r, i) => (
                        <tr key={`${r.vehicleNumber}-${r.doc}-${i}`}>
                          <td style={S.td}>{r.vehicleNumber}</td>
                          <td style={S.td}>{r.doc}</td>
                          <td style={S.td}>
                            {r.expiryYmd}
                            <span
                              style={{
                                ...S.badge(r.status === "Expired" ? "bad" : "warn"),
                                marginLeft: 6,
                              }}
                            >
                              {r.status}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div>
            <div style={S.section}>Upcoming maintenance</div>
            <div style={S.panel}>
              <div style={S.tableScroll}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Vehicle</th>
                      <th style={S.th}>Type</th>
                      <th style={S.th}>Next service</th>
                    </tr>
                  </thead>
                  <tbody>
                    {serviceUpcoming.length === 0 ? (
                      <tr>
                        <td style={S.td} colSpan={3}>
                          No next-service dates in this period.
                        </td>
                      </tr>
                    ) : (
                      serviceUpcoming.slice(0, 12).map((r, i) => (
                        <tr key={`${r.vehicleNumber}-${r.nextServiceYmd}-${i}`}>
                          <td style={S.td}>{r.vehicleNumber}</td>
                          <td style={S.td}>{r.maintenanceType}</td>
                          <td style={S.td}>
                            {r.nextServiceYmd}
                            <span style={{ ...S.badge(r.daysLeft < 0 ? "bad" : r.daysLeft <= 14 ? "warn" : "ok"), marginLeft: 6 }}>
                              {r.daysLeft < 0 ? "Overdue" : `${r.daysLeft}d`}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>


        <VmOverviewRecordTables
          S={S}
          fmtInr={fmtInr}
          filterMode={filterMode}
          periodTitle={periodTitle}
          recordsSectionTitle={recordsSectionTitle}
          maintSectionTitle={maintSectionTitle}
          dieselRecordGrid={dieselRecordGrid}
          dieselNotFilled={dieselNotFilled}
          maintenanceRecordGrid={maintenanceRecordGrid}
        />

        <div style={S.section}>Per-vehicle summary — {periodTitle}</div>
        <div style={S.panel}>
          <div style={S.tableScroll}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Vehicle</th>
                  <th style={S.th}>Owner</th>
                  <th style={S.th}>Type</th>
                  <th style={S.th}>Sched refill</th>
                  <th style={S.th}>Docs</th>
                  <th style={S.th}>Next diesel</th>
                  <th style={S.th}>Diesel</th>
                  <th style={S.th}>Maintenance</th>
                  <th style={S.th}>Expenses</th>
                  <th style={S.th}>Total</th>
                </tr>
              </thead>
              <tbody>
                {perVehicle.length === 0 ? (
                  <tr>
                    <td style={S.td} colSpan={10}>
                      {loading ? "Loading…" : "No onboarded vehicles."}
                    </td>
                  </tr>
                ) : (
                  perVehicle.map((r) => {
                    const total = r.dieselAmount + r.maintAmount + r.expenseAmount;
                    return (
                      <tr key={r.vehicleNumber}>
                        <td style={S.td}><b>{r.vehicleNumber}</b></td>
                        <td style={S.td}>{r.ownerName}</td>
                        <td style={S.td}>{r.vehicleType}</td>
                        <td style={S.td}>{r.scheduleInterval}</td>
                        <td style={S.td}>
                          <span style={S.badge(
                            r.docStatus === "Expired" ? "bad" : r.docStatus === "Expiring Soon" ? "warn" : "ok",
                          )}>{r.docStatus}</span>
                        </td>
                        <td style={S.td}>
                          {r.nextDieselDue !== "—" ? (
                            <>
                              {r.nextDieselDue}
                              <div style={{ fontSize: 10, color: "#666" }}>{r.nextDieselStatus}</div>
                            </>
                          ) : "—"}
                        </td>
                        <td style={S.td}>
                          {r.dieselCount ? `${r.dieselCount} · ${fmtInr(r.dieselAmount)}` : "—"}
                        </td>
                        <td style={S.td}>
                          {r.maintCount ? `${r.maintCount} · ${fmtInr(r.maintAmount)}` : "—"}
                        </td>
                        <td style={S.td}>
                          {r.expenseCount ? `${r.expenseCount} · ${fmtInr(r.expenseAmount)}` : "—"}
                        </td>
                        <td style={{ ...S.td, fontWeight: 700 }}>{total > 0 ? fmtInr(total) : "—"}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div style={S.section}>Quick links</div>
        <div style={S.tiles}>
          {tiles.map((t) => (
            <div
              key={t.path}
              style={S.tile}
              onClick={() => nav(t.path)}
              onKeyDown={(e) => e.key === "Enter" && nav(t.path)}
              role="button"
              tabIndex={0}
            >
              <div style={{ fontSize: 32, marginBottom: 8 }}>{t.icon}</div>
              <div style={{ fontWeight: "bold", fontSize: 13 }}>{t.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
